import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CommunicationFeedback } from "@/services/interfaces/IFeedbackService";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Star, User } from "lucide-react";
import { GuestNameWithFlag } from "@/components/pages/reservations/guests/components/GuestNameWithFlag";
import { PresignedImageWithPlaceholder } from "@/components/common/PresignedImageWithPlaceholder";
import { cn } from "@/lib/utils";
import { ChatSection } from "./ChatSection";

interface CommunicationFeedbackModalProps {
  feedback: CommunicationFeedback | null;
  isOpen: boolean;
  onClose: () => void;
}

export const CommunicationFeedbackModal: React.FC<
  CommunicationFeedbackModalProps
> = ({ feedback, isOpen, onClose }) => {
  const { t } = useTranslation();

  if (!feedback) return null;

  const guest = feedback.guest;
  const booking = feedback.booking;
  const concierge = feedback.consierge;

  const guestName =
    `${guest?.profile.firstName || ""} ${guest?.profile.lastName || ""}`.trim();
  const countryCode = guest?.profile.language || "";
  const roomInfo = `${t("feedbacks.modal.room", "room")} ${booking?.room?.number || ""} ${booking?.room?.category?.name || ""}`;

  const arrivalDate = booking?.arrivalDate
    ? new Date(booking.arrivalDate)
    : null;
  const departureDate = booking?.departureDate
    ? new Date(booking.departureDate)
    : null;
  const feedbackDate = feedback.createdAt ? new Date(feedback.createdAt) : null;

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            height={14}
            width={14}
            key={i}
            className={cn(
              "h-5 w-5",
              i < rating
                ? "fill-black text-black"
                : "fill-gray-200 text-gray-200",
            )}
          />
        ))}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] gap-0">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-center">
            {t("feedbacks.modal.communicationTitle", "Communication Feedback")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Guest Info */}
          <div className="flex items-center justify-center gap-2">
            <GuestNameWithFlag
              className="text-gray-5"
              name={guestName}
              countryCode={countryCode}
            />
            <span className="text-gray-5">, {roomInfo}</span>
          </div>

          {/* Reservation Number */}
          <div className="flex gap-4 items-center">
            <span className="text-gray-5 text-sm">
              {t("feedbacks.modal.reservationNumber", "Reservation number")}
            </span>
            <span className="font-medium">
              {booking?.bookingNumber || "N/A"}
            </span>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-8">
            <div className="flex gap-2 items-center">
              <span className="text-gray-5 text-sm">
                {t("feedbacks.modal.arrival", "Guest arrival")}
              </span>
              <div>
                <div className="font-medium">
                  {arrivalDate ? format(arrivalDate, "dd.MM.yyyy") : "N/A"}
                </div>
                <div className="text-gray-5">
                  {arrivalDate ? format(arrivalDate, "HH:mm") : ""}
                </div>
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <span className="text-gray-5 text-sm">
                {t("feedbacks.modal.departure", "Guest departure")}
              </span>
              <div>
                <div className="font-medium">
                  {departureDate ? format(departureDate, "dd.MM.yyyy") : "N/A"}
                </div>
                <div className="text-gray-5">
                  {departureDate ? format(departureDate, "HH:mm") : ""}
                </div>
              </div>
            </div>
          </div>

          {/* Concierge */}
          <div className="flex items-center gap-4">
            <span className="text-gray-5 min-w-[100px] text-sm">
              {t("feedbacks.modal.concierge", "Concierge")}
            </span>
            <div className="flex flex-col items-center">
              <div className="h-12 w-12 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center mb-1">
                {concierge?.photoId ? (
                  <PresignedImageWithPlaceholder
                    photo={concierge.photoId}
                    alt={concierge.firstName || ""}
                    placeholder={<User className="h-6 w-6 text-gray-400" />}
                  />
                ) : (
                  <User className="h-6 w-6 text-gray-400" />
                )}
              </div>
              <span className="font-medium text-sm">
                {concierge
                  ? `${concierge.firstName} ${concierge.lastName}`
                  : t("feedbacks.modal.noAssignedConcierge", "-")}
              </span>
            </div>
          </div>

          {/* Feedback */}
          <div className="flex flex-col items-start gap-4">
            <span className="text-gray-5 min-w-[100px] text-sm">
              {t("feedbacks.modal.feedback", "Feedback")}
            </span>
            <span className="font-medium">
              &ldquo;
              {feedback.comment ||
                t("feedbacks.modal.noComments", "No comments.")}
              &rdquo;
            </span>
          </div>

          {/* Evaluations */}
          <div className="flex items-center justify-between">
            <span className="text-gray-5 text-sm">
              {t("feedbacks.modal.evaluations", "Evaluations")}
            </span>
            {renderStars(feedback.rating)}
          </div>

          {/* Date */}
          <div className="flex items-center justify-between">
            <span className="text-gray-5 text-sm">
              {t("feedbacks.modal.date", "Date")}
            </span>
            <div className="flex gap-2 font-medium">
              <span>
                {feedbackDate ? format(feedbackDate, "dd.MM.yyyy") : "N/A"}
              </span>
              <span className="text-gray-5">
                {feedbackDate ? format(feedbackDate, "HH:mm") : ""}
              </span>
            </div>
          </div>

          {/* Chat Section */}
          <ChatSection
            conciergeName={
              concierge ? `${concierge.firstName} ${concierge.lastName}` : ""
            }
            chatId={feedback.chatId}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
